import AsyncStorage from '@react-native-async-storage/async-storage';
import JailMonkey from 'utils/packages/jailMonkey';
import { all, call, delay, fork, put, putResolve, select, takeLatest, } from 'typed-redux-saga/macro';
import { fetchAnalyticsBudgetingData, getAdditionalUserInfo, getAdsAction, getBankConnections, getBreaches, getCategories, getFeatureFlags, getFeed, getGroups, getModalNotifications, getMonitoredAccounts, getMonthlyTotals, getRemovedSpaces, getSmartRules, getSpaceInvites, getTags, getTransactions, getUser, getWebSignInQRCodeStatus, } from 'actions';
import { selectHasExpiringConnection, selectFeatureFlag, selectIsGBUser, selectIsLoggedIn, selectPendingSpaceInvites, selectUserAdditionalInfo, selectActiveConnectionsWithAllAccountsClosed, selectHasSetPaydayRange, } from 'reducers/selectors';
import { getFxFees, getRequiredNationalIdentifiers, getSecclAUMFees, getTradingAccounts, updateWealthAccounts, } from 'actions/invest';
import { FETCH_APP_DATA, FETCH_DASHBOARD_MODAL_STACK, fetchAppDataComplete, holdAppOpenModals, } from 'actions/refresh';
import { setAdAction, setShouldSeeDeviceRootedWarningModal, } from 'actions/sessionSettings';
import { REGISTER_USER_SUCCESS, SET_CONNECTIONS_STATUS, UPDATE_KEYCHAIN_USER, } from 'actions/types';
import { getBudgets } from 'features/budgeting/actions';
import { selectAlpacaAccountStatus, selectHasCountryOfTaxResidence, selectSecclAccountStatus, selectShouldShowMigrationToSecclBanner, selectShowSecclFlow, } from 'features/invest/reducers/selectors';
import { getActiveReferralProgram, getCreditReferrals, } from 'features/inviteFriend/actions';
import { selectShouldShowNetScore } from 'features/net-promoter-score/selectors';
import { getPotsInformation, getSavingsPlan } from 'features/pots/actions';
import { getStripeCustomer } from 'features/premium/actions/stripe';
import { selectPaymentFailed, selectSeatBasedPaymentFailed, selectShouldShowStripe, selectStripePaymentMethodMissing, } from 'features/premium/selectors';
import { checkPromotions, checkReverseTrial } from 'features/promotions/sagas';
import { getQuests } from 'features/quests/actions';
import { getRentReporting } from 'features/rent-reporting/actions';
import { selectHasCurrentRentReporting } from 'features/rent-reporting/selectors';
import { DEVICE_ROOTED_KEY } from 'features/security/components/RootedDeviceWarningModal';
import { fetchCommittedForCurrentPaydayRange, getSubscriptions, } from 'features/subscriptions/actions';
import { selectIncludeIncomeSubscriptions } from 'features/subscriptions/selectors';
import { FETCH_SPACES } from 'features/spaces/constants';
import { getIntroductoryCancellationOffer, } from 'features/promotions/actions';
import { Tier } from 'features/premium/entities';
import { navigationRef } from 'utils/navigationv6';
import { selectShouldForceOnboardingReport } from 'features/onboarding-report/cohort';
import { getCreditAndDebt } from 'features/pay/actions/payments';
import { selectUserTier } from 'features/premium/selectors/tier';
import { getExtraMemberCancelledInvites, getExtraMemberInviteRequests, getSharedPlans, } from 'actions/sharedPlans';
import { YEARLY_REPORT_INTRO } from 'actions/deviceSettingTypes';
import { getVPNStatus } from 'features/NordVpn/actions';
import { format, startOfMonth, subMonths } from 'date-fns';
import { requestCreditScoreFetchFlow } from 'features/credit-score/actions';
import { selectIsInvestWalkthroughActive } from 'reducers/selectors/walkthrough';
import { getDefaultPaymentsAccount } from 'features/pay/actions';
import { migratePassCodeSaga } from './migratePassCodeSaga';
import { updateKeychainUserSaga } from './updateKeychainUserSaga';
import { bundleId, isAndroid, isWeb } from '../constants';
export const checkJailBroken = async () => {
    try {
        if (JailMonkey.isJailBroken()) {
            const val = await AsyncStorage.getItem(DEVICE_ROOTED_KEY);
            if (val !== 'true') {
                return true;
            }
        }
    }
    catch {
        //
    }
    return false;
};
export function* setupDashboardStackWorker() {
    const isShowingOnboardingReport = yield* select(selectShouldForceOnboardingReport);
    if (isShowingOnboardingReport) {
        yield* put(holdAppOpenModals());
        yield* put(fetchAppDataComplete());
        return;
    }
    const tierBeforeDowngradingToBasic = yield* select((store) => store.user.tierBeforeDowngradingToBasic);
    if (tierBeforeDowngradingToBasic) {
        const hasPaydayRange = yield* select(selectHasSetPaydayRange);
        yield* put(getMonthlyTotals({
            dateFrom: format(startOfMonth(subMonths(new Date(), 12)), 'yyyy-MM-dd'),
            dateTo: format(new Date(), 'yyyy-MM-dd'),
            isPayDay: hasPaydayRange,
            step: undefined,
            reset: { totals: true, filteredTotals: false },
            withPreviousPlan: true,
        }));
    }
    const walkthroughState = yield* select((store) => store.feed.walkthrough ?? {});
    const isInvestWalkthroughActive = yield* select(selectIsInvestWalkthroughActive);
    if (walkthroughState.status === 'start' && !isInvestWalkthroughActive) {
        // On first load the user shouldn’t actually have any other modals, so we can show this without delay
        navigationRef.navigateDeprecated({
            name: 'WalkthroughModal',
            params: { allowSkip: walkthroughState.allowSkip ?? false },
        });
    }
    const [spaceInvite] = yield* select(selectPendingSpaceInvites);
    if (spaceInvite) {
        navigationRef.navigateDeprecated('SpaceInvite', {
            inviteId: spaceInvite.id,
            spaceId: spaceInvite.spaceId,
        });
    }
    const signInSpace = yield* select((store) => store.signInNavigation?.space);
    if (!spaceInvite && signInSpace) {
        navigationRef.navigateDeprecated('JoinSpace', signInSpace);
    }
    const shouldShowStripe = yield* select(selectShouldShowStripe);
    const emmaProStatus = yield* select((store) => store.user.user.emmaProStatus);
    // We only show the 'PremiumSubscriptionPaymentFailed' based on this logic passing so doesn’t need to be sync if false
    const makeStripeCustomerCallSync = emmaProStatus.autoRenewing && emmaProStatus.store === 'stripe';
    const showRentQuestion = yield* select((store) => selectFeatureFlag(store, 'in_app_rent_question').value);
    const isGBUser = yield* select(selectIsGBUser);
    const userAdditionalInfoBeforeRefresh = yield* select(selectUserAdditionalInfo);
    const makeRentReportingCall = isGBUser;
    // If the user has not yet answered the rent question we must do the call sync so we can show the screen
    // as part of the dashboard stack
    const makeRentReportingCallSync = makeRentReportingCall &&
        (userAdditionalInfoBeforeRefresh?.isRenting === null ||
            userAdditionalInfoBeforeRefresh?.isRenting === undefined);
    const discountGiftAfterOnboarding = yield* select((store) => selectFeatureFlag(store, 'discount_gift_after_onboarding').value);
    const tier = yield* select(selectUserTier);
    const shouldSeeIntroductoryDiscountOffer = yield* select((state) => state.deviceSettings.shouldSeeIntroductoryGiftDiscountScreen);
    const didSeeSecclMigrationIntro = yield* select((state) => state.deviceSettings.didViewSecclMigrationIntro);
    const isSecclEnabled = yield* select(selectShowSecclFlow);
    const alpacaAccountStatus = yield* select(selectAlpacaAccountStatus);
    const secclAccountStatus = yield* select(selectSecclAccountStatus);
    const shouldMakeGetTradingAccountsCall = isGBUser &&
        isSecclEnabled &&
        !didSeeSecclMigrationIntro &&
        (alpacaAccountStatus === 'ACTIVE' || alpacaAccountStatus === 'CLOSED') &&
        secclAccountStatus === 'NOT_STARTED';
    const [discountOfferResult, promotionsResult, isJailBroken, adsResult, notificationsResult, reverseTrialResult,] = yield* all([
        shouldSeeIntroductoryDiscountOffer &&
            tier === Tier.free &&
            shouldShowStripe &&
            discountGiftAfterOnboarding
            ? putResolve(getIntroductoryCancellationOffer(isAndroid ? 'android' : 'ios', isAndroid && bundleId === 'com.emma.dev' ? 'dev' : undefined))
            : undefined,
        call(checkPromotions), // This is to check for available discounts
        call(checkJailBroken), // This is to check jailbroken and display the jailbreak modal
        putResolve(getAdsAction()), // This is to get the ads
        putResolve(getModalNotifications()),
        call(checkReverseTrial),
        shouldShowStripe
            ? (makeStripeCustomerCallSync ? putResolve : put)(getStripeCustomer())
            : undefined,
        (makeRentReportingCallSync ? putResolve : put)(getAdditionalUserInfo()),
        makeRentReportingCall // if makeRentReportingCall is true we have to getRentReporting in a sync way
            ? (makeRentReportingCallSync ? putResolve : put)(getRentReporting({ withRentTransactions: false, isActive: true }))
            : undefined,
        isGBUser ? putResolve(getCreditReferrals()) : undefined,
        isGBUser && shouldShowStripe && tier === Tier.ultimate
            ? putResolve(getSharedPlans())
            : undefined,
        tier === Tier.free ? putResolve(getExtraMemberInviteRequests()) : undefined,
        isGBUser && tier === Tier.ultimate && shouldShowStripe
            ? putResolve(getExtraMemberCancelledInvites())
            : undefined,
        shouldMakeGetTradingAccountsCall
            ? putResolve(getTradingAccounts())
            : undefined,
    ]);
    const shouldShowNetScore = yield* select(selectShouldShowNetScore); // Controls the NPS modal
    // todo how can we get full type safety here?
    // const ad = (adsResult as unknown as GetAdsSuccessAction)?.payload?.popUp;
    const modalNotifications = notificationsResult?.payload?.notifications;
    const routes = [];
    const reconsentRequired = yield* select(selectHasExpiringConnection);
    const refreshConnectionViewed = yield* select((store) => store.signInNavigation?.refreshConnectionViewed);
    const shouldShowConsentScreen = reconsentRequired && !refreshConnectionViewed;
    const userAdditionalInfo = yield* select(selectUserAdditionalInfo);
    // The Alpaca migration screen is not on web
    if (isGBUser && isSecclEnabled && !didSeeSecclMigrationIntro && !isWeb) {
        const shouldShowSecclIntro = yield* select(selectShouldShowMigrationToSecclBanner);
        if (shouldShowSecclIntro) {
            routes.push({
                name: 'MigrationToSecclIntro',
                params: undefined,
            });
        }
    }
    const invites = yield* select((state) => state.sharedPlans.invites);
    if (invites.length && tier === Tier.free) {
        routes.push({
            name: 'ExtraMembersInvite',
            params: undefined,
        });
    }
    const rejectedInvites = yield* select((state) => state.sharedPlans.rejectedInvites);
    if (rejectedInvites.length &&
        tier === Tier.ultimate &&
        isGBUser &&
        shouldShowStripe) {
        routes.push({
            name: 'RejectedExtraMembersInvites',
            params: undefined,
        });
    }
    const feed = yield* select((store) => store.feed.feed);
    const creditReferrals = yield* select((store) => store.referrals.creditReferrals);
    creditReferrals?.referrals.forEach((ref) => {
        if (ref.status === 'COMPLETED' && !ref.seenAt) {
            routes.push({
                name: 'CreditReferralDetails',
                params: {
                    id: ref.id,
                },
            });
        }
    });
    const didViewYearlyReportIntro = yield* select((store) => store.deviceSettings[YEARLY_REPORT_INTRO]);
    const yearlyReportData = feed.cards.yearlyReportCoverInfo;
    if (yearlyReportData && !didViewYearlyReportIntro) {
        routes.push({
            name: 'YearlyReportIntro',
            params: {
                data: { ...yearlyReportData, type: 'YEARLY' },
            },
        });
    }
    const hasCurrentRentReporting = yield* select(selectHasCurrentRentReporting);
    if (showRentQuestion &&
        !hasCurrentRentReporting &&
        userAdditionalInfo &&
        userAdditionalInfo?.isRenting == null) {
        routes.push({
            name: 'RentQuestion',
            params: undefined,
        });
    }
    if (shouldShowConsentScreen) {
        routes.push({
            name: 'LoginsReconsent',
            params: { isModal: true },
        });
    }
    if (shouldShowNetScore) {
        routes.push({
            name: 'NPS',
            params: undefined,
        });
    }
    if (modalNotifications?.length) {
        routes.push({
            name: 'ModalNotificationScreen',
            params: {
                id: modalNotifications[0].id,
            },
        });
    }
    if (tierBeforeDowngradingToBasic) {
        routes.push({
            name: 'DowngradeToBasicInfoScreen',
            params: undefined,
        });
    }
    const activeConnectionsWithAllAccountsClosed = yield* select(selectActiveConnectionsWithAllAccountsClosed);
    const ignoredCloseConnectionPrompts = yield* select((store) => store.user.ignoredCloseConnectionPrompts);
    if (activeConnectionsWithAllAccountsClosed.length) {
        activeConnectionsWithAllAccountsClosed.forEach((connection) => {
            if (ignoredCloseConnectionPrompts[connection.id])
                return;
            routes.push({
                name: 'ConnectionAccountsClosed',
                params: { connection },
            });
        });
    }
    if (reverseTrialResult === 'start') {
        routes.push({ name: 'ReverseTrialIntroScreen', params: undefined });
    }
    else if (reverseTrialResult === 'ended') {
        routes.push({ name: 'ReverseTrialEndedScreen', params: undefined });
    }
    const discountOffer = discountOfferResult?.payload?.stripeOffers?.find((offer) => offer.discount === 50);
    const discountCurrency = discountOfferResult?.payload?.currency;
    if (discountOffer && discountCurrency) {
        routes.push({
            name: 'DiscountGiftScreen',
            params: {
                currency: discountCurrency,
                stripeOffer: {
                    periods: discountOffer.periods,
                    couponId: discountOffer.couponId,
                    discount: discountOffer.discount,
                    productId: discountOffer.productId,
                    discountPrice: discountOffer.discountPrice,
                },
            },
        });
    }
    else if (typeof promotionsResult === 'number') {
        routes.push({
            name: 'PromoDiscountScreen',
            params: { promotionId: promotionsResult },
        });
    }
    const paymentFailed = yield* select(selectPaymentFailed);
    const missingPaymentMethod = yield* select(selectStripePaymentMethodMissing);
    const canUseStripe = yield* select(selectShouldShowStripe);
    if (canUseStripe && (!!paymentFailed || missingPaymentMethod)) {
        routes.push({
            name: 'PremiumSubscriptionPaymentFailed',
            params: {},
        });
    }
    const seatBasedPaymentFailed = yield* select(selectSeatBasedPaymentFailed);
    if (canUseStripe && !!seatBasedPaymentFailed) {
        routes.push({
            name: 'SeatBasedSubscriptionPaymentFailed',
            params: {},
        });
    }
    const didExtraMemberSubscriptionEnd = yield* select((state) => state.user.didExtraMemberSubscriptionEnd);
    if (tier === Tier.free && didExtraMemberSubscriptionEnd) {
        routes.push({
            name: 'ExtraMemberPlanExpired',
            params: {},
        });
    }
    const didEmployerSubscriptionEnd = yield* select((state) => state.user.employerSubscriptionEndInfo?.didEmployerSubscriptionEnd);
    if (tier === Tier.free && didEmployerSubscriptionEnd) {
        routes.push({
            name: 'ManageOrganisationSubscriptionExpired',
            params: {},
        });
    }
    const isLoggedIn = yield* select(selectIsLoggedIn);
    if (isLoggedIn) {
        routes.forEach((routeObj) => {
            navigationRef.navigateDeprecated(routeObj);
        });
    }
    yield* delay(500);
    /**
     * Wait for navigation to end before triggering these modals
     * so that they done render on top of the above screens
     */
    if (isJailBroken) {
        yield* put(setShouldSeeDeviceRootedWarningModal(true));
    }
    const ad = adsResult?.payload?.popUp;
    if (ad?.id !== undefined) {
        yield* put(setAdAction(ad));
    }
    yield* put(getRemovedSpaces());
    if (isGBUser) {
        yield* put(getDefaultPaymentsAccount());
    }
    yield* put(fetchAppDataComplete());
}
function* fetchAppData() {
    // This needs to go without delay
    // TODO why no delay on this? surely this is less important that user/fl
    yield* put(getTransactions({ page: 1, hasInternalTransactions: false }));
    const feed = yield* select((store) => store.feed.feed);
    const promisesArray = [
        putResolve(getUser(undefined, { withWalkthrough: true })),
        putResolve(getBankConnections()),
        putResolve(getActiveReferralProgram()),
        putResolve(getFeatureFlags()),
        putResolve(getSpaceInvites('PENDING')),
        putResolve(getSharedPlans()),
        ...(!feed.fetchedInOnboarding ? [putResolve(getFeed())] : []),
        delay(500),
    ];
    // We load in 'essential' data first
    yield* all(promisesArray);
    yield* call(setupDashboardStackWorker);
    yield* delay(500);
    const user = yield* select((store) => store.user.user);
    log(`[fetchAppData] User ID is ${user.id}`, false, 'cyan');
    yield* put({ type: FETCH_SPACES });
    if (user.guessedHomeCountry === 'GB') {
        yield* put(getPotsInformation());
    }
    yield* fork(migratePassCodeSaga, user);
    yield* put({ type: UPDATE_KEYCHAIN_USER });
    yield* put(getMonitoredAccounts());
    yield* put(getBreaches());
    yield* put(fetchAnalyticsBudgetingData());
    yield* put(fetchCommittedForCurrentPaydayRange());
    yield* put(getSubscriptions(yield* select(selectIncludeIncomeSubscriptions)));
    yield* put(getSmartRules());
    yield* put(getGroups());
    yield* put(getQuests());
    yield* put(getCreditAndDebt());
    yield* put(getTags());
    yield* put(getCategories());
    yield* put(getVPNStatus());
    const webSignInConfirmation = yield* select((store) => store.signInWeb.webSignInConfirmation);
    if (webSignInConfirmation?.clientId) {
        yield* put(getWebSignInQRCodeStatus({
            clientId: webSignInConfirmation.clientId,
        }));
    }
    const isGBUser = yield* select(selectIsGBUser);
    if (isGBUser) {
        yield* put(requestCreditScoreFetchFlow());
        yield* put(getFxFees());
        yield* put(getSecclAUMFees());
        yield* put(getSavingsPlan()); // TODO replace with get saving plan fees
        const countryOfTaxResidence = yield* select(selectHasCountryOfTaxResidence);
        if (countryOfTaxResidence) {
            yield* put(getRequiredNationalIdentifiers());
        }
        yield* put(updateWealthAccounts({
            fetchPendingTopups: true,
        }));
    }
}
function* syncCompleteSaga(action) {
    if (action.payload) {
        return;
    }
    // Analytics and feed sagas will refresh themselves
    yield* put(getBudgets());
    yield* put(fetchCommittedForCurrentPaydayRange());
    yield* put(getSubscriptions(yield* select(selectIncludeIncomeSubscriptions)));
}
function* checkAndFetchAppData(action) {
    if (action.extra.isLeaveFlow) {
        yield* call(fetchAppData);
    }
}
function* refreshSaga() {
    yield* takeLatest(FETCH_APP_DATA, fetchAppData);
    yield* takeLatest('REMOVE_EXTRA_MEMBER_SUCCESS', checkAndFetchAppData);
    yield* takeLatest(FETCH_DASHBOARD_MODAL_STACK, setupDashboardStackWorker);
    yield* takeLatest(SET_CONNECTIONS_STATUS, syncCompleteSaga);
    yield* takeLatest((action) => [REGISTER_USER_SUCCESS, UPDATE_KEYCHAIN_USER].includes(action.type), updateKeychainUserSaga);
}
export default refreshSaga;
